<script lang="ts" setup>
import { Quiz } from '@/models/Quiz';
import QuizTileComponent from '@/components/QuizTileComponent.vue';
import EmailComponent from '@/components/EmailComponent.vue';
import NewsletterComponent from '@/components/NewsletterComponent.vue';
import DataPrivacyOverlayComponent from '@/components/DataPrivacyOverlayComponent.vue';

const props = defineProps<{
  quizzes: Array<Quiz>,
  overlayIsVisible: boolean,
  hasProgress: boolean,
}>();

const emit = defineEmits<{
  (e: 'reset'): void,
  (e: 'setIsLoading', value: boolean): void,
  (e: 'toggleOverlay', value: boolean): void,
  (e: 'setQuiz', quizId: string): void,
}>();

const setQuiz = (id: string) => {
  emit('setQuiz', id);
};

const reset = () => {
  emit('reset');
};

const setIsLoading = (event: boolean) => {
  emit('setIsLoading', event);
};

const onToggleOverlay = (event: boolean) => {
  emit('toggleOverlay', event);
};
</script>


<template>
  <div class="home">
    <!-- Quizzes -->
    <div class="row">
      <div class="column">
        <h1 class="center">Überprüfe dein Wissen mit einem Quiz</h1>
        <div class="grid grid--quiz">
          <QuizTileComponent
            v-for="quiz in props.quizzes"
            v-bind:key="quiz.id"
            v-on:setQuiz="setQuiz($event)"
            :quiz="quiz"
          />
        </div>

        <!-- Reset -->
        <div class="reset center">
          <button
            v-on:click="reset()"
            :disabled="hasProgress === false"
          >
            Fortschritt zurücksetzen
          </button>
        </div>
      </div>
    </div>

    <!-- Newsletter -->
    <div class="row">
      <div class="column">
        <NewsletterComponent />
      </div>
    </div>

    <!-- Email contact -->
    <div class="row">
      <div class="column">
        <EmailComponent
            :overlayIsVisible="props.overlayIsVisible"
            v-on:setIsLoading="setIsLoading($event)"
            v-on:toggleOverlay="onToggleOverlay($event)"
        />
      </div>
    </div>

    <!-- Info Overlay -->
    <DataPrivacyOverlayComponent
        v-if="props.overlayIsVisible"
        v-on:toggleOverlay="onToggleOverlay($event)"
    />
  </div>
</template>


<style lang="scss" scoped>
@import '@/assets/scss/layout.scss';
@import '@/assets/scss/buttons.scss';
@import '@/assets/scss/breakpoints.scss';

.grid {
  display: grid;
  gap: 2em;
  grid-template-columns: 1fr 1fr 1fr;
  justify-content: space-around;
}

h2 {
  + p {
    margin-bottom: 2em;
  }

  &.mb-05 {
    margin-bottom: 0.5em;
  }
}

.reset {
  margin-top: 2em;
}

.center {
  text-align: center;
}

.xxl {
  font-size: $font-size-xl;
  margin-bottom: 0.5em;
}

.video {
  margin-top: 1em;
}

.cover {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  background: url('~@/assets/img/video-cover.jpg');
  background-size: cover;
  z-index: 1;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  &:hover {
    cursor: pointer;
  }

  .play-button {
    width: 128px;
    height: 128px;
    background: url('~@/assets/img/play-button.png');
    background-size: cover;
  }

  &:focus-visible {
    outline: $outline;

    .play-button {
      border: $outline;
      border-radius: $outline;
      border-radius: 50%;
    }
  }
}

@media screen and (max-width: $breakpoint-tablet) {
  .grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media screen and (max-width: $breakpoint-phone) {
  .grid {
    grid-template-columns: 1fr;
  }
}
</style>
